import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../api";
import { BrutalCard, BrutalInput } from "../components/Brutal";
import { MagnifyingGlass, ShieldCheck, Warning, ArrowRight, UsersThree } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_STYLES = {
  owner: "bg-zinc-900 text-white",
  cfo: "bg-[#c9a24a] text-zinc-950",
  partner: "bg-emerald-600 text-white",
  distributor: "bg-zinc-100 text-zinc-700 border border-zinc-200",
};

function AuditCard({ u }) {
  const { t } = useTranslation();
  return (
    <BrutalCard className="p-4 space-y-3" data-testid={`audit-card-${u.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold truncate">{u.name}</span>
            <span className={`inline-flex px-2 py-0.5 text-[10px] font-medium rounded-full ${ROLE_STYLES[u.role] || ROLE_STYLES.distributor}`}>
              {t(`roles.${u.role}`, u.role)}
            </span>
            {u.distributor_code && (
              <span className="text-[10px] font-mono text-zinc-500">{u.distributor_code}</span>
            )}
            {u.is_personal && (
              <span className="text-[10px] px-1.5 py-0.5 bg-violet-50 text-violet-700 border border-violet-200 rounded-full">
                {t("user_audit.personal_badge")}
              </span>
            )}
          </div>
          <p className="text-xs text-zinc-500 truncate">{u.email}</p>
          {u.chain?.length > 0 && (
            <p className="text-[11px] text-zinc-400 mt-0.5 flex items-center gap-1 flex-wrap">
              {u.chain.map((n, i) => (
                <span key={i} className="inline-flex items-center gap-1">
                  {i > 0 && <ArrowRight size={10} />}
                  {n}
                </span>
              ))}
              <ArrowRight size={10} />
              <span className="font-medium text-zinc-600">{u.name}</span>
            </p>
          )}
        </div>
        {u.healthy ? (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full shrink-0" data-testid={`audit-healthy-${u.id}`}>
            <ShieldCheck size={12} weight="bold" /> {t("user_audit.healthy")}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold bg-red-50 text-red-700 border border-red-300 rounded-full shrink-0" data-testid={`audit-mismatch-${u.id}`}>
            <Warning size={12} weight="bold" /> {t("user_audit.mismatch")}
          </span>
        )}
      </div>

      {u.products.length > 0 ? (
        <div className="border border-zinc-200 rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 text-zinc-500">
              <tr>
                <th className="text-left px-2 py-1.5 font-medium">{t("user_audit.product")}</th>
                <th className="text-right px-2 py-1.5 font-medium">{t("user_audit.held")}</th>
                <th className="text-right px-2 py-1.5 font-medium">{t("user_audit.expected")}</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {u.products.map((p) => (
                <tr key={p.name} className={`border-t border-zinc-100 ${p.match ? "" : "bg-red-50/60"}`}>
                  <td className="px-2 py-1.5">{p.name}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{p.held}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{p.expected}</td>
                  <td className="px-2 py-1 text-center">
                    {p.match
                      ? <ShieldCheck size={12} className="inline text-emerald-600" />
                      : <Warning size={12} weight="bold" className="inline text-red-600" />}
                  </td>
                </tr>
              ))}
              <tr className="border-t border-zinc-200 bg-zinc-50 font-semibold">
                <td className="px-2 py-1.5">{t("user_audit.total")}</td>
                <td className="px-2 py-1.5 text-right font-mono">{u.held_total}</td>
                <td className="px-2 py-1.5 text-right font-mono">{u.expected_total}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-xs text-zinc-400">{t("user_audit.no_stock")}</p>
      )}

      {u.possible_duplicates.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg px-3 py-2 text-[11px] text-amber-800" data-testid={`audit-dupes-${u.id}`}>
          <span className="font-semibold">{t("user_audit.duplicates_warning")}: </span>
          {u.possible_duplicates.map((d) => `${d.name} (${t(`roles.${d.role}`, d.role)})`).join(", ")}
        </div>
      )}
    </BrutalCard>
  );
}

export default function UserAuditPage() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/user-audit", { params: { q: query } });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const mismatched = (data?.users || []).filter((u) => !u.healthy).length;

  return (
    <div className="space-y-6" data-testid="user-audit-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2">
          <UsersThree size={26} weight="bold" /> {t("user_audit.title")}
        </h1>
        <p className="text-sm sm:text-base text-zinc-600 mt-1">{t("user_audit.subtitle")}</p>
      </div>

      <form
        className="flex gap-2 max-w-md"
        onSubmit={(e) => { e.preventDefault(); load(q); }}
      >
        <div className="relative flex-1">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <BrutalInput
            data-testid="user-audit-search"
            className="pl-8"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("user_audit.search_placeholder")}
          />
        </div>
      </form>

      {loading ? (
        <p className="text-sm text-zinc-500">{t("common.loading")}</p>
      ) : (
        <>
          <p className="text-xs text-zinc-500" data-testid="user-audit-summary">
            {t("user_audit.summary", { total: data?.count || 0, mismatched })}
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {(data?.users || []).map((u) => <AuditCard key={u.id} u={u} />)}
          </div>
          {(data?.users || []).length === 0 && (
            <p className="text-sm text-zinc-400">{t("user_audit.no_results")}</p>
          )}
        </>
      )}
    </div>
  );
}
